import { Alert, Badge, Box, Button } from '@palmetto/palmetto-components';
import { useFormikContext } from 'formik';
import { useEffect, useMemo } from 'react';

import { AttestmentTypeLabelMap, DomesticContentAttestmentType, ITCQualificationResponse } from 'types';

import { ManufacturerWarnings } from '../../ITCSystemDesign/ManufacturerWarnings';

export interface ITCCalculatorResultsProps {
  data: ITCQualificationResponse | undefined;
  currentDomesticContentValues?: {
    attestmentType?: DomesticContentAttestmentType;
    calculationResult?: ITCQualificationResponse;
  };
  reset?: () => void;
  simple?: boolean;
  showDomesticContent?: boolean;
}

export const formatPercentageOrUndefined = (value: number | undefined, showZero = false): string =>
  (value && value > 0) || showZero ? `${value?.toFixed(1) ?? 0}%` : '--';

const BadgeResult = ({ isQualified }: { isQualified?: boolean }) => {
  if (isQualified === undefined) {
    return <Badge variant="default" message="No" size="lg" />;
  }
  return isQualified ? (
    <Badge variant="success" message="Yes" size="lg" />
  ) : (
    <Badge variant="danger" message="No" size="lg" />
  );
};

interface GridRowProps {
  children: React.ReactNode;
  bold?: boolean;
  isFirst?: boolean;
}

const GridRow = ({ children, bold, isFirst }: GridRowProps) => (
  <Box
    direction="row"
    borderWidth={isFirst ? '0' : 'xs 0 0 0'}
    borderColor="separator"
    style={{ fontWeight: bold ? 'bold' : 'normal' }}
  >
    {children}
  </Box>
);

interface GridCellProps {
  children: React.ReactNode;
  width?: string;
  isFirst?: boolean;
  textAlign?: React.CSSProperties['textAlign'];
}

const GridCell = ({ children, width = '25%', isFirst, textAlign }: GridCellProps) => (
  <Box padding="sm md" style={{ width, textAlign }} borderWidth={isFirst ? '0' : '0 0 0 xs'} borderColor="separator">
    {children}
  </Box>
);

export const ITCCalculatorResults = ({
  data,
  currentDomesticContentValues,
  simple = false,
  showDomesticContent = true,
  reset,
}: ITCCalculatorResultsProps) => {
  const { dirty } = useFormikContext<any>();
  const calculationValues = data ?? currentDomesticContentValues?.calculationResult;

  const feocThresholdDisplay = useMemo(() => {
    if (!calculationValues) return '';

    const pvThreshold = formatPercentageOrUndefined(calculationValues.pvSystemFeocPercentageThreshold, true);
    const storageThreshold = calculationValues.batteryIncluded
      ? ` / ${formatPercentageOrUndefined(calculationValues.batteryFeocPercentageThreshold, true)} Storage`
      : '';

    return `Non-FEOC - ${pvThreshold} PV${storageThreshold}`;
  }, [calculationValues]);

  useEffect(() => {
    if (data && dirty && typeof reset === 'function') {
      reset();
    }
  }, [data, dirty, reset]);

  useEffect(() => {
    const resultsEl = document.getElementById('dc-results-table');
    if (resultsEl) {
      setTimeout(() => {
        resultsEl.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [calculationValues]);

  if (!calculationValues) {
    return null;
  }

  const componentWidth = '50%';
  const valueWidth = showDomesticContent ? '25%' : '50%';

  return (
    <Box padding="lg" fontSize="md" style={{ maxWidth: '50em' }}>
      <Box>
        <Box as="h2" fontSize="lg" margin="0 0 sm" id="dc-results-table" style={{ fontWeight: 'bold' }}>
          Calculator Results
        </Box>
        <Box as="p" margin="0 0 md">
          Based on the equipment combination below, this system would qualify as follows:
        </Box>
      </Box>

      {/* Component Percentages Grid */}
      <Box borderWidth="xs" borderColor="separator" radius="0">
        <GridRow bold isFirst>
          <GridCell width={componentWidth} isFirst textAlign="center">
            Component
          </GridCell>
          <GridCell width={valueWidth} textAlign="center">
            % Non-FEOC
          </GridCell>
          {showDomesticContent && (
            <GridCell width={valueWidth} textAlign="center">
              DC %
            </GridCell>
          )}
        </GridRow>
        {!simple && (
          <>
            <GridRow>
              <GridCell width={componentWidth} isFirst>
                Panel:
              </GridCell>
              <GridCell width={valueWidth}>
                {formatPercentageOrUndefined(calculationValues?.panelFeocPercentage)}
              </GridCell>
              {showDomesticContent && (
                <GridCell width={valueWidth}>
                  {formatPercentageOrUndefined(calculationValues?.panelPercentage)}
                </GridCell>
              )}
            </GridRow>
            <GridRow>
              <GridCell width={componentWidth} isFirst>
                Inverter:
              </GridCell>
              <GridCell width={valueWidth}>
                {formatPercentageOrUndefined(calculationValues?.inverterFeocPercentage)}
              </GridCell>
              {showDomesticContent && (
                <GridCell width={valueWidth}>
                  {formatPercentageOrUndefined(calculationValues?.inverterPercentage)}
                </GridCell>
              )}
            </GridRow>
            <GridRow>
              <GridCell width={componentWidth} isFirst>
                Rail:
              </GridCell>
              <GridCell width={valueWidth}>
                {formatPercentageOrUndefined(calculationValues?.rackingRailFeocPercentage)}
              </GridCell>
              {showDomesticContent && (
                <GridCell width={valueWidth}>
                  {formatPercentageOrUndefined(calculationValues?.rackingRailPercentage)}
                </GridCell>
              )}
            </GridRow>
            <GridRow>
              <GridCell width={componentWidth} isFirst>
                <span id="itc-result-racking-fasteners-cell">Racking - Fasteners:</span>
              </GridCell>
              <GridCell width={valueWidth}>
                {formatPercentageOrUndefined(calculationValues?.rackingTotalFastenerFeocPercentage)}
              </GridCell>
              {showDomesticContent && (
                <GridCell width={valueWidth}>
                  {formatPercentageOrUndefined(calculationValues?.rackingTotalFastenerPercentage)}
                </GridCell>
              )}
            </GridRow>
            <GridRow>
              <GridCell width={componentWidth} isFirst>
                Racking - Production Credit:
              </GridCell>
              <GridCell width={valueWidth}>
                {formatPercentageOrUndefined(calculationValues?.rackingBonusFeocPercentage)}
              </GridCell>
              {showDomesticContent && (
                <GridCell width={valueWidth}>
                  {formatPercentageOrUndefined(calculationValues?.rackingBonusPercentage)}
                </GridCell>
              )}
            </GridRow>
          </>
        )}
        {currentDomesticContentValues?.attestmentType && (
          <GridRow>
            <GridCell width={showDomesticContent ? '75%' : '50%'} isFirst>
              Current Domestic Content Type:
            </GridCell>
            <GridCell width={valueWidth}>
              {AttestmentTypeLabelMap[currentDomesticContentValues.attestmentType]}
            </GridCell>
          </GridRow>
        )}
        <GridRow bold>
          <GridCell width={componentWidth} isFirst>
            Total - PV:
          </GridCell>
          <GridCell width={valueWidth}>
            {formatPercentageOrUndefined(calculationValues?.pvTotalFeocPercentage, true)}
          </GridCell>
          {showDomesticContent && (
            <GridCell width={valueWidth}>
              {formatPercentageOrUndefined(calculationValues?.pvTotalPercentage, true)}
            </GridCell>
          )}
        </GridRow>
        {calculationValues?.batteryIncluded && (
          <GridRow bold>
            <GridCell width={componentWidth} isFirst>
              Total - Storage:
            </GridCell>
            <GridCell width={valueWidth}>
              {formatPercentageOrUndefined(calculationValues?.batteryFeocPercentage, true)}
            </GridCell>
            {showDomesticContent && (
              <GridCell width={valueWidth}>
                {formatPercentageOrUndefined(calculationValues?.batteryPercentage, true)}
              </GridCell>
            )}
          </GridRow>
        )}
      </Box>

      {/* Meets Eligibility Threshold Grid */}
      <Box borderWidth="xs" borderColor="separator" radius="0" margin="md 0 0">
        <GridRow isFirst>
          <GridCell width="100%" isFirst textAlign="center">
            <strong>Meets Eligibility Threshold</strong>
          </GridCell>
        </GridRow>
        <GridRow bold>
          <GridCell width={showDomesticContent ? '50%' : '100%'} isFirst textAlign="center">
            {feocThresholdDisplay}
          </GridCell>
          {showDomesticContent && (
            <GridCell width="50%" textAlign="center">
              DC - {formatPercentageOrUndefined(calculationValues?.pvSystemPercentageThreshold, true)}
            </GridCell>
          )}
        </GridRow>
        <GridRow>
          <GridCell width={showDomesticContent ? '50%' : '100%'} isFirst>
            <span>
              PV: <BadgeResult isQualified={calculationValues?.pvFeocQualified} />
            </span>
          </GridCell>
          {showDomesticContent && (
            <GridCell width="50%">
              <span>
                PV: <BadgeResult isQualified={calculationValues?.pvQualified} />
              </span>
            </GridCell>
          )}
        </GridRow>
        {calculationValues?.batteryIncluded && (
          <GridRow>
            <GridCell width={showDomesticContent ? '50%' : '100%'} isFirst>
              <span>
                PV+Storage: <BadgeResult isQualified={calculationValues?.batteryFeocQualified} />
              </span>
            </GridCell>
            {showDomesticContent && (
              <GridCell width="50%">
                <span>
                  PV+Storage: <BadgeResult isQualified={calculationValues?.batteryQualified} />
                </span>
              </GridCell>
            )}
          </GridRow>
        )}
        <GridRow>
          <GridCell width="100%" isFirst>
            <strong>Eligibility Thresholds for Year:</strong> {calculationValues?.safeHarborYear ?? 'N/A'}
          </GridCell>
        </GridRow>
      </Box>

      <ManufacturerWarnings isAccountSpecific={true} fetchedResults={!!calculationValues} />
      <Box margin="xs 0" childGap="sm">
        {data?.problems?.map((problem: string, index: number) => (
          <Alert key={`problem-${index}`} variant="danger" hasIcon title={problem} />
        ))}
      </Box>
      <Box margin="xs 0" childGap="sm">
        {data?.warnings?.map((warning: string, index: number) => (
          <Alert key={`warning-${index}`} variant="warning" hasIcon title={warning} />
        ))}
      </Box>
      <Box margin="xs 0" childGap="sm">
        {data?.feocExceptionGranted || data?.equipmentExceptionGranted ? (
          <Alert
            variant="warning"
            hasIcon
            title={'This account qualifies for a FEOC exception. Click "Next" to continue.'}
          />
        ) : null}
      </Box>
      <Box direction="row" justifyContent="flex-end">
        {!simple && data && (
          <Button size="md" variant="secondary" onClick={reset}>
            Edit Equipment
          </Button>
        )}
      </Box>
    </Box>
  );
};
